import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Alert,
  LinearProgress,
  useTheme,
} from '@mui/material'
import {
  ArrowUpward as InIcon,
  ArrowDownward as OutIcon,
  CalendarMonth as CalendarIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  QrCode as QRIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import toast from 'react-hot-toast'
import { usePagination, useLoading } from '../../hooks'
import { formatDate } from '../../utils/formatters'
import { StatusChip } from '../../components/common/Chips'
import { attendanceApi } from '../../api/attendanceApi'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'

const EmployeeAttendance = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { user, orgId } = useSelector((state) => state.auth)
  const { loading, withLoading } = useLoading()
  const pagination = usePagination()
  const [selectedMonth, setSelectedMonth] = useState(new Date())
  const [attendanceData, setAttendanceData] = useState({
    todayStatus: {
      date: formatDate(new Date()),
      checkIn: '-',
      checkOut: '-',
      status: 'Pending',
      workHours: 0,
      isLate: false,
      overtime: 0,
      location: '-',
    },
    monthlyStats: {
      present: 22,
      absent: 2,
      late: 2,
      leaves: 3,
      earlyOut: 1,
      overtimeHours: 8,
      totalWorkingDays: 26,
    },
    attendanceRecords: [],
  })
  const [totalRecords, setTotalRecords] = useState(0)

  useEffect(() => {
    fetchTodayStatus()
  }, [user, orgId])

  useEffect(() => {
    fetchHistory()
  }, [pagination.page, pagination.rowsPerPage, user, orgId])

  const fetchTodayStatus = async () => {
    withLoading(async () => {
      try {
        // Fetch today's attendance
        const response = await attendanceApi.getTodayAttendance({
          'X-Employee-ID': user?.id,
          'X-Org-ID': orgId
        })

        if (response.data) {
          const data = response.data
          setAttendanceData(prev => ({
            ...prev,
            todayStatus: {
              date: data.date || formatDate(new Date()),
              checkIn: data.check_in || '-',
              checkOut: data.check_out || '-',
              status: data.status || 'Pending',
              workHours: data.work_hours || 0,
              isLate: data.is_late || false,
              overtime: data.overtime || null, // Placeholder for backend
              location: data.location || '-',
            }
          }))
        }
      } catch (error) {
        console.error('Error fetching attendance:', error)
        toast.error('Failed to fetch attendance data')
      }
    })
  }

  const fetchHistory = async () => {
    try {
      const response = await attendanceApi.getAttendanceHistory({
        page: pagination.page + 1,
        limit: pagination.rowsPerPage
      }, {
        'X-Employee-ID': user?.id,
        'X-Org-ID': orgId
      })

      if (response.data) {
        const { records, pagination: paginationData } = response.data
        setAttendanceData(prev => ({
          ...prev,
          attendanceRecords: records.map(record => ({
            id: record.id,
            date: record.date,
            checkIn: record.check_in || '-',
            checkOut: record.check_out || '-',
            status: record.status,
            workHours: record.work_hours,
            isLate: false, // Not provided by API yet
            overtime: record.overtime,
            location: record.location || '-'
          }))
        }))
        setTotalRecords(paginationData.total)
      }
    } catch (error) {
      console.error('Error fetching history:', error)
      toast.error('Failed to fetch attendance history')
    }
  }

  const fetchAttendanceData = async () => {
    withLoading(async () => {
      await Promise.all([
        fetchTodayStatus(),
        fetchHistory()
      ])
    })
  }

  const handleMarkAttendance = (type) => {
    navigate('/employee/test-face-io', { state: { type } })
  }

  const calculateAttendancePercentage = () => {
    const { present, totalWorkingDays } = attendanceData.monthlyStats
    return totalWorkingDays > 0 ? Math.round((present / totalWorkingDays) * 100) : 0
  }

  const today = new Date()
  const canCheckIn = !attendanceData.todayStatus.checkIn || attendanceData.todayStatus.checkIn === '-'
  const canCheckOut = attendanceData.todayStatus.checkIn && attendanceData.todayStatus.checkIn !== '-' &&
    (!attendanceData.todayStatus.checkOut || attendanceData.todayStatus.checkOut === '-')

  // Apply pagination
  // Apply pagination
  const paginatedRecords = attendanceData.attendanceRecords

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Attendance Management
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Track your daily attendance, view history, and manage your time
          </Typography>
        </Box>

        {/* Today's Attendance Card */}
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 4, backgroundColor: theme.palette.mode === 'dark' ? 'action.hover' : '#f8f9fa' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Today's Attendance
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formatDate(today, 'EEEE, MMMM d, yyyy')}
              </Typography>
            </Box>
            <StatusChip status={attendanceData.todayStatus.status} />
          </Box>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Mark Attendance
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
                    <Button
                      variant="contained"
                      startIcon={<InIcon />}
                      onClick={() => handleMarkAttendance('check-in')}
                      disabled={!canCheckIn || loading}
                      sx={{ flex: 1, py: { xs: 1.5, sm: 1 } }}
                    >
                      {loading ? 'Processing...' : 'Check In'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<OutIcon />}
                      onClick={() => handleMarkAttendance('check-out')}
                      disabled={!canCheckOut || loading}
                      sx={{ flex: 1, py: { xs: 1.5, sm: 1 } }}
                    >
                      Check Out
                    </Button>
                  </Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Office Hours:</strong> 9:00 AM - 6:30 PM
                    </Typography>
                    <Typography variant="body2">
                      <strong>Late Arrival:</strong> After 9:15 AM
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Today's Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2" color="textSecondary">Check-in Time</Typography>
                      <Typography variant="h6">
                        {attendanceData.todayStatus.checkIn}
                        {attendanceData.todayStatus.isLate && (
                          <Chip
                            label="Late"
                            size="small"
                            color="error"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2" color="textSecondary">Check-out Time</Typography>
                      <Typography variant="h6">{attendanceData.todayStatus.checkOut}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2" color="textSecondary">Work Hours</Typography>
                      <Typography variant="h6">{attendanceData.todayStatus.workHours ? `${attendanceData.todayStatus.workHours} hrs` : '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2" color="textSecondary">Overtime</Typography>
                      <Typography variant="h6" color={attendanceData.todayStatus.overtime > 0 ? "success.main" : "textSecondary"}>
                        {attendanceData.todayStatus.overtime !== null && attendanceData.todayStatus.overtime > 0
                          ? `+${attendanceData.todayStatus.overtime} hrs`
                          : '-'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="textSecondary">Location</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2">{attendanceData.todayStatus.location}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Attendance History */}
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">Attendance History</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button startIcon={<DownloadIcon />} variant="outlined">
                Export
              </Button>
              <Button startIcon={<FilterIcon />} variant="outlined">
                Filter
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Check-in</TableCell>
                  <TableCell>Check-out</TableCell>
                  <TableCell>Work Hours</TableCell>
                  <TableCell>Overtime</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableSkeleton columns={7} rows={pagination.rowsPerPage} />
                ) : paginatedRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CalendarIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body1" color="textSecondary">
                        No attendance records found
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Your attendance history will appear here once you start checking in
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : paginatedRecords.map((record) => (
                  <TableRow key={record.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {formatDate(record.date, 'EEE, MMM d')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatDate(record.date)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {record.checkIn !== '-' && (
                          <>
                            <InIcon fontSize="small" color="action" />
                            <Typography>{record.checkIn}</Typography>
                            {record.isLate && (
                              <Chip label="Late" size="small" color="error" />
                            )}
                          </>
                        )}
                        {record.checkIn === '-' && (
                          <Typography color="textSecondary">-</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {record.checkOut !== '-' && (
                          <>
                            <OutIcon fontSize="small" color="action" />
                            <Typography>{record.checkOut}</Typography>
                          </>
                        )}
                        {record.checkOut === '-' && (
                          <Typography color="textSecondary">-</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography>{record.workHours ? `${record.workHours} hrs` : '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color={record.overtime > 0 ? 'success.main' : 'textSecondary'}>
                        {record.overtime > 0 ? `+${record.overtime} hrs` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={record.status} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{record.location}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalRecords}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={pagination.handleChangePage}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          />
        </Paper>

        {/* Quick Actions */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<CalendarIcon />}
            onClick={() => navigate('/employee/attendance/history')}
          >
            View Detailed History
          </Button>
        </Box>
      </Paper>
    </Box>
  )
}

export default EmployeeAttendance